import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CheckCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { platformApi } from '../api'
import type { InboxItem } from '../types'

/** Items waiting on the signed-in user's decision. */
export default function InboxPage() {
    const [items, setItems] = useState<InboxItem[] | null>(null)
    useEffect(() => {
        platformApi.inbox().then(setItems).catch((e) => { toast.error(e.message); setItems([]) })
    }, [])
    return (
        <div className="flex flex-col gap-6">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Approvals</h1>
                <p className="text-sm text-muted-foreground">Requests waiting for your decision.</p>
            </div>
            {items === null ? (
                <div className="flex flex-col gap-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24" />)}</div>
            ) : items.length === 0 ? (
                <Empty>
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><CheckCheck /></EmptyMedia>
                        <EmptyTitle>You're all caught up</EmptyTitle>
                        <EmptyDescription>Nothing is waiting for your review.</EmptyDescription>
                    </EmptyHeader>
                </Empty>
            ) : (
                items.map((s) => (
                    <Link key={s.id} to="/submissions/$id" params={{ id: s.id }} className="block rounded-xl focus-visible:outline-2 focus-visible:outline-ring">
                        <Card className="transition-shadow hover:shadow-md">
                            <CardHeader className="flex-row items-start justify-between gap-3">
                                <div>
                                    <CardTitle className="text-base">{s.form_name}</CardTitle>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {s.submitter_name} · <span className="font-mono text-xs">{s.number}</span>
                                    </p>
                                </div>
                                <Badge>{s.step_name || `Step ${s.current_step}`}</Badge>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground">
                                {s.title && <p className="truncate">{s.title}</p>}
                                <p>Submitted {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}
                                    {s.finding_count > 0 && ` · ${s.finding_count} finding${s.finding_count === 1 ? '' : 's'}`}</p>
                            </CardContent>
                        </Card>
                    </Link>
                ))
            )}
        </div>
    )
}
